import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { LoadingBlock } from '../../components/Spinner'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { useLibraryDeps } from '../../library/LibraryDeps'
import { ListLoading } from '../../library/ListLoading'
import { Sentinel } from '../../library/Sentinel'
import { useMediaSrc } from '../../library/useMediaSrc'
import { useToast } from '../../library/useToast'
import { MediaViewer } from '../../library/vault/MediaViewer'
import type { VaultItem } from '../../library/vault/vaultData'
import { Avatar } from '../Avatar'
import { OfHtml } from '../OfHtml'
import { createOfApi, OF } from '../ofApi'
import { MediaThumb, PostCard } from '../PostCard'
import { compact, endOfDayMarker, toPost, toUser, type OfUser, type Post } from '../posts'
import { useCursorList, type CursorPage } from '../useCursorList'
import { useUsers } from '../useUsers'
import {
  DEFAULT_QUERY,
  isDefaultView,
  postsPath,
  toCounters,
  toHighlight,
  toLabel,
  toProfile,
  type Counters,
  type Highlight,
  type Label,
  type MediaFilter,
  type PostQuery,
  type Profile,
  type SortDir,
  type SortField,
} from './profileData'
import '../../library/library.css'
import '../ofPages.css'
import './profile.css'

interface Props {
  accountId: string
  canWrite: boolean
  /** Another CRM page of this Account (Statistics from the header menu, Settings → Profile from Edit profile). */
  onOpenPage: (page: string, query?: string) => void
  mobileNav?: ReactNode
}

type PostsPayload = {
  list?: Record<string, unknown>[]
  hasMore?: boolean
  headMarker?: string
  tailMarker?: string
  counters?: unknown
}

const postId = (p: Post) => p.id
const PAGE = 10

/** My profile: the Account's own OnlyFans profile page (OnlyFans /<username>, captured 2026-10-08). */
export function ProfilePage({ accountId, canWrite, onOpenPage, mobileNav }: Props) {
  const { t } = useTranslation('ofProfile')
  const { gateway } = useLibraryDeps()
  const of = useMemo(() => createOfApi(gateway), [gateway])
  const { say, node: toast } = useToast()
  const mobile = useIsNarrow()

  // ── The profile ──────────────────────────────────────────────────────────
  const [profile, setProfile] = useState<Profile | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let live = true
    setProfile(null)
    setFailed(false)
    ;(async () => {
      const me = await of.get<Record<string, unknown>>('users/me', {}, OF)
      const username = String(me?.username ?? '')
      const raw = username ? await of.get<Record<string, unknown>>(`users/${username}`, {}, `${OF}/${username}`) : me
      if (live) setProfile(toProfile(raw ?? {}))
    })().catch(() => live && setFailed(true))
    return () => {
      live = false
    }
    // accountId: another Account is another profile.
  }, [of, accountId])

  const referer = profile ? `${OF}/${profile.username}` : OF
  const owner: OfUser | undefined = useMemo(
    () => (profile ? { id: profile.id, name: profile.name, username: profile.username, avatar: profile.avatar, verified: profile.verified } : undefined),
    [profile],
  )
  const { users, resolve, remember } = useUsers(of, referer)
  useEffect(() => {
    if (owner) remember(owner)
  }, [owner, remember])

  // ── Side data: labels, highlights, friends, recent media ────────────────
  const [labels, setLabels] = useState<Label[]>([])
  const [highlights, setHighlights] = useState<Highlight[]>([])
  const [friends, setFriends] = useState<OfUser[]>([])
  const [recent, setRecent] = useState<VaultItem[]>([])
  useEffect(() => {
    if (!profile) return
    const id = profile.id
    let live = true
    const list = (p: unknown) => ((p as { list?: Record<string, unknown>[] } | null)?.list ?? [])
    of.get(`users/${id}/labels`, { limit: 10, offset: 0, 'non-empty': 1 }, referer)
      .then((p) => live && setLabels(list(p).map(toLabel)))
      .catch(() => undefined)
    of.get(`users/${id}/stories/highlights`, { limit: 5, offset: 0, sort: 'recent:desc' }, referer)
      .then((p) => live && setHighlights(list(p).map(toHighlight)))
      .catch(() => undefined)
    of.get(`users/${id}/friends`, { limit: 10, offset: 0 }, referer)
      .then((p) => live && setFriends(list(p).map(toUser)))
      .catch(() => undefined)
    const recentOf = (kind: 'photos' | 'videos') =>
      of
        .get(`users/${id}/posts/${kind}`, { limit: 3, order: 'publish_date_desc', skip_users: 'all', only_can_view: 1, format: 'infinite', pinned_sort: 0, counters: 1 }, referer)
        .then((p) => list(p).map(toPost))
        .catch(() => [] as Post[])
    void Promise.all([recentOf('photos'), recentOf('videos')]).then(([a, b]) => {
      if (!live) return
      const posts = [...a, ...b].sort((x, y) => y.postedAt.localeCompare(x.postedAt))
      setRecent(posts.flatMap((p) => p.media).slice(0, 3))
    })
    return () => {
      live = false
    }
  }, [of, profile?.id, referer]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Posts ────────────────────────────────────────────────────────────────
  const [q, setQ] = useState<PostQuery>(DEFAULT_QUERY)
  const [counters, setCounters] = useState<Counters | null>(null)
  const [head, setHead] = useState<string | null>(null)
  const [gridPosts, setGridPosts] = useState(false)
  const [gridMedia, setGridMedia] = useState(true)
  const grid = q.tab === 'posts' ? gridPosts : gridMedia
  const plain = isDefaultView(q)

  const fetchPosts = useCallback(
    async (cursor: string | null): Promise<CursorPage<Post>> => {
      if (!profile) return { list: [], hasMore: false, next: null }
      const p = await of.get<PostsPayload>(
        postsPath(profile.id, q),
        {
          limit: PAGE,
          order: `${q.field}_${q.dir}`,
          skip_users: 'all',
          format: 'infinite',
          ...(q.label ? { label: q.label } : {}),
          ...(q.search ? { query: q.search } : {}),
          ...(plain ? { pinned: 0 } : {}),
          counters: cursor ? 0 : 1,
          ...(cursor ? { beforePublishTime: cursor } : q.date ? { beforePublishTime: endOfDayMarker(q.date) } : {}),
        },
        q.tab === 'media' ? `${referer}/media` : referer,
      )
      if (!cursor) {
        const c = toCounters(p?.counters)
        if (c) setCounters(c)
        setHead(q.date ? (p?.headMarker ?? null) : null)
      }
      return { list: (p?.list ?? []).map(toPost), hasMore: p?.hasMore === true, next: p?.tailMarker ?? null }
    },
    [of, profile, q, plain, referer],
  )
  const posts = useCursorList(fetchPosts, postId)

  // Pinned posts lead the plain Posts view.
  const [pinned, setPinned] = useState<Post[]>([])
  const [pinnedToken, setPinnedToken] = useState(0)
  useEffect(() => {
    if (!profile || q.tab !== 'posts' || !plain) {
      setPinned([])
      return
    }
    let live = true
    of.get<PostsPayload>(`users/${profile.id}/posts`, { skip_users: 'all', pinned: 1, counters: 0, format: 'infinite' }, referer)
      .then((p) => live && setPinned((p?.list ?? []).map(toPost)))
      .catch(() => live && setPinned([]))
    return () => {
      live = false
    }
  }, [of, profile, q.tab, plain, referer, pinnedToken])

  // After "Go to date": newer posts load on demand ("Load previous posts").
  const [loadingNewer, setLoadingNewer] = useState(false)
  const loadNewer = async () => {
    if (!profile || !head || loadingNewer) return
    setLoadingNewer(true)
    try {
      const p = await of.get<PostsPayload>(
        postsPath(profile.id, q),
        { limit: PAGE, order: `${q.field}_${q.dir}`, skip_users: 'all', format: 'infinite', counters: 0, afterPublishTime: head },
        referer,
      )
      const newer = (p?.list ?? []).map(toPost)
      posts.setItems((all) => [...newer.filter((n) => !all.some((x) => x.id === n.id)), ...all])
      setHead(p?.hasMore ? (p?.headMarker ?? null) : null)
    } catch {
      say(t('error'))
    } finally {
      setLoadingNewer(false)
    }
  }

  const shown = useMemo(() => {
    const pins = new Set(pinned.map((p) => p.id))
    return [...pinned, ...posts.items.filter((p) => !pins.has(p.id))]
  }, [pinned, posts.items])

  const change = (next: Post) => {
    posts.setItems((all) => all.map((p) => (p.id === next.id ? next : p)))
    setPinned((all) => all.map((p) => (p.id === next.id ? next : p)))
  }
  const removePost = (id: number, raw?: Record<string, unknown>) => {
    posts.setItems((all) => all.filter((p) => p.id !== id))
    setPinned((all) => all.filter((p) => p.id !== id))
    const c = toCounters(raw)
    if (c) setCounters(c)
    else setCounters((c0) => (c0 ? { ...c0, posts: Math.max(0, c0.posts - 1) } : c0))
  }

  // ── Viewer ───────────────────────────────────────────────────────────────
  const [viewer, setViewer] = useState<{ items: VaultItem[]; index: number } | null>(null)
  const openMedia = (items: VaultItem[], index: number) => setViewer({ items, index })

  // ── Header actions ───────────────────────────────────────────────────────
  const [menu, setMenu] = useState<null | 'header' | 'status' | 'sort'>(null)
  const act = async (work: () => Promise<void>) => {
    try {
      await work()
    } catch (e) {
      say(e instanceof Error && e.message ? e.message : t('error'))
    }
  }
  const setStatus = (visible: boolean) =>
    act(async () => {
      setMenu(null)
      if (!profile || profile.visibleOnline === visible) return
      await of.send('PATCH', 'users/me', referer, { isVisibleOnline: visible })
      setProfile({ ...profile, visibleOnline: visible })
    })
  const copyProfileLink = () => {
    setMenu(null)
    if (!profile) return
    void navigator.clipboard?.writeText(`${OF}/${profile.username}`).then(
      () => say(t('linkCopied')),
      () => say(t('error')),
    )
  }

  const setQuery = (patch: Partial<PostQuery>) => setQ((cur) => ({ ...cur, ...patch }))
  const [searchText, setSearchText] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const runSearch = (text: string) => {
    setSearchText(text)
    setQuery({ search: text.trim() })
  }

  if (failed) {
    return (
      <div className={`lib ofp ${mobile ? 'lib--m' : ''}`}>
        <p className="lib-empty ofp-fail">{t('profile.loadFailed')}</p>
        {mobile && mobileNav}
      </div>
    )
  }
  if (!profile || !owner) {
    return (
      <div className={`lib ofp ${mobile ? 'lib--m' : ''}`}>
        <div className="ofp-loading">
          <LoadingBlock label={t('loading')} />
        </div>
        {mobile && mobileNav}
      </div>
    )
  }

  const tabCount = (n: number | undefined) => (n === undefined ? '' : n.toLocaleString())
  const headerMenu = menu === 'header' && (
    <>
      <div className="lib-backdrop" onClick={() => setMenu(null)} />
      <div className="lib-pop lib-menu lib-menu--right" role="menu" aria-label={t('profile.actions')}>
        <button type="button" role="menuitem" className="lib-menu-item" onClick={() => (setMenu(null), onOpenPage('statistics'))}>
          <span className="lib-menu-label">{t('profile.statistics')}</span>
        </button>
        <button type="button" role="menuitem" className="lib-menu-item" onClick={copyProfileLink}>
          <span className="lib-menu-label">{t('profile.copyLink')}</span>
        </button>
      </div>
    </>
  )

  const sortMenu = menu === 'sort' && (
    <>
      <div className="lib-backdrop" onClick={() => setMenu(null)} />
      <div className="lib-pop lib-menu lib-menu--right ofp-sort" role="menu" aria-label={t('sort.label')}>
        <SortItem on={!q.date} label={t('sort.allTime')} onPress={() => (setMenu(null), setQuery({ date: null }))} />
        <label className="lib-menu-item ofp-date-item">
          <span className="lib-menu-label">{t('sort.goToDate')}</span>
          <input
            type="date"
            className="ofp-date"
            aria-label={t('sort.goToDate')}
            max={new Date().toISOString().slice(0, 10)}
            value={q.date ?? ''}
            onChange={(e) => (setMenu(null), setQuery({ date: e.target.value || null, field: 'publish_date' }))}
          />
        </label>
        <div className="ofp-menu-sep" />
        {(['publish_date', 'favorites_count', 'tips_summ'] as SortField[]).map((f) => (
          <SortItem key={f} on={q.field === f} label={t(`sort.${f}`)} onPress={() => (setMenu(null), setQuery({ field: f, ...(f !== 'publish_date' ? { date: null } : {}) }))} />
        ))}
        <div className="ofp-menu-sep" />
        {(['asc', 'desc'] as SortDir[]).map((d) => (
          <SortItem key={d} on={q.dir === d} label={t(`sort.${d}`)} onPress={() => (setMenu(null), setQuery({ dir: d }))} />
        ))}
      </div>
    </>
  )

  const posts0 = counters?.posts
  const media0 = counters?.medias
  const chips =
    q.tab === 'posts' ? (
      labels.length > 0 && (
        <div className="lib-chips ofp-chips" role="radiogroup" aria-label={t('profile.labels')}>
          <Chip on={!q.label} label={t('profile.all')} n={posts0} onPress={() => setQuery({ label: null })} />
          {labels.map((l) => (
            <Chip key={l.id} on={q.label === l.id} label={l.name} n={l.posts} onPress={() => setQuery({ label: l.id })} />
          ))}
        </div>
      )
    ) : (
      <div className="lib-chips ofp-chips" role="radiogroup" aria-label={t('profile.mediaType')}>
        {(['medias', 'photos', 'videos'] as MediaFilter[]).map((m) => (
          <Chip
            key={m}
            on={q.media === m}
            label={t(`profile.media.${m}`)}
            n={m === 'medias' ? counters?.medias : m === 'photos' ? counters?.photos : counters?.videos}
            onPress={() => setQuery({ media: m })}
          />
        ))}
      </div>
    )

  const noPosts = !posts.loading && !posts.hasMore && shown.length === 0
  const list = grid ? (
    <div className="ofp-grid">
      {shown.flatMap((p) =>
        q.tab === 'media'
          ? p.media.map((m, i) => (
              <MediaThumb key={`${p.id}-${m.id}`} item={m} onOpen={() => openMedia(p.media, i)} label={t('post.openMedia')} />
            ))
          : [<PostTile key={p.id} post={p} onOpen={() => (p.media.length ? openMedia(p.media, 0) : setGridPosts(false))} />],
      )}
    </div>
  ) : (
    <div className="ofp-posts">
      {shown.map((p) => (
        <PostCard
          key={p.id}
          post={p}
          author={users[p.authorId] ?? owner}
          ownerId={profile.id}
          accountId={accountId}
          canWrite={canWrite}
          of={of}
          referer={referer}
          users={users}
          resolveUsers={resolve}
          onChange={change}
          onRemove={removePost}
          onPinned={() => setPinnedToken((n) => n + 1)}
          onOpenMedia={openMedia}
          say={say}
        />
      ))}
    </div>
  )

  const main = (
    <>
      <ProfileHeader
        profile={profile}
        canWrite={canWrite}
        menu={menu}
        setMenu={setMenu}
        headerMenu={headerMenu}
        onStatus={setStatus}
        onCopyLink={copyProfileLink}
        onEdit={() => onOpenPage('settings', 's=profile')}
        highlights={highlights}
      />
      <div className="ofp-tabs" role="tablist" aria-label={t('profile.tabs')}>
        {(['posts', 'media'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={q.tab === tab}
            className={`ofp-tab ${q.tab === tab ? 'is-on' : ''}`}
            onClick={() => setQuery({ tab, label: null })}
          >
            {t(`profile.tab.${tab}`, { n: tabCount(tab === 'posts' ? posts0 : media0) })}
          </button>
        ))}
      </div>
      <div className="ofp-filter">
        <span className="ofp-filter-title">{q.search ? t('profile.searchFor', { q: q.search }) : t(q.date ? 'profile.fromDate' : `profile.order.${q.field}`, { date: q.date ?? '' })}</span>
        <span className="lib-spacer" />
        {mobile && (
          <button type="button" className="lib-icon-btn" aria-label={t('profile.search')} aria-expanded={searchOpen} onClick={() => setSearchOpen((o) => !o)}>
            <FeatherIcon name="search" size={17} />
          </button>
        )}
        <button
          type="button"
          className="lib-icon-btn"
          aria-pressed={grid}
          aria-label={grid ? t('profile.fullView') : t('profile.compactView')}
          title={grid ? t('profile.fullView') : t('profile.compactView')}
          onClick={() => (q.tab === 'posts' ? setGridPosts(!grid) : setGridMedia(!grid))}
        >
          <FeatherIcon name={grid ? 'square' : 'grid'} size={17} />
        </button>
        <div className="lib-anchor">
          <button type="button" className="lib-icon-btn" aria-label={t('sort.label')} aria-expanded={menu === 'sort'} onClick={() => setMenu(menu === 'sort' ? null : 'sort')}>
            <FeatherIcon name="bar-chart-2" size={17} className="ofp-rot90" />
          </button>
          {sortMenu}
        </div>
      </div>
      {mobile && searchOpen && <SearchBox value={searchText} onSearch={runSearch} />}
      {chips}
      {q.search && (
        <button type="button" className="ofp-link-btn ofp-clear-search" onClick={() => runSearch('')}>
          {t('profile.clearSearch')}
        </button>
      )}
      {head && (
        <button type="button" className="ofp-wide-btn" disabled={loadingNewer} onClick={() => void loadNewer()}>
          {t('profile.loadPrevious')}
        </button>
      )}
      {list}
      {noPosts && <p className="lib-empty">{q.search || q.label ? t('profile.noMatch') : t('profile.noPosts')}</p>}
      {posts.failed && <p className="lib-empty">{t('error')}</p>}
      <ListLoading loading={posts.loading} empty={!shown.length} />
      {posts.hasMore && !posts.failed && <Sentinel onVisible={posts.loadMore} token={posts.items.length} />}
    </>
  )

  return (
    <div className={`lib ofp ${mobile ? 'lib--m ofp--m' : ''}`}>
      <div className="ofp-scroll">
        <div className="ofp-columns">
          <main className="ofp-center">{main}</main>
          {!mobile && (
            <aside className="ofp-aside" aria-label={t('profile.aside')}>
              <SearchBox value={searchText} onSearch={runSearch} />
              {recent.length > 0 && (
                <section className="ofp-card">
                  <h3 className="ofp-card-title">{t('profile.recent')}</h3>
                  <div className="ofp-recent">
                    {recent.map((m, i) => (
                      <MediaThumb key={m.id} item={m} onOpen={() => openMedia(recent, i)} label={t('post.openMedia')} />
                    ))}
                  </div>
                </section>
              )}
              {friends.length > 0 && (
                <section className="ofp-card">
                  <h3 className="ofp-card-title">{t('profile.friends')}</h3>
                  <div className="ofp-friends">
                    {friends.map((f) => (
                      <a key={f.id} className="ofp-friend" href={`${OF}/${f.username}`} target="_blank" rel="noopener noreferrer">
                        <Avatar user={f} size={56} />
                        <span className="ofp-friend-name">{f.name}</span>
                        <span className="ofp-friend-handle">@{f.username}</span>
                      </a>
                    ))}
                  </div>
                </section>
              )}
              <section className="ofp-card">
                <h3 className="ofp-card-title">{t('profile.subscription')}</h3>
                <div className="ofp-sub-row">
                  <span>{t('profile.subscriptionPrice')}</span>
                  <span className="ofp-muted">{profile.subscribePrice > 0 ? t('profile.perMonth', { price: profile.subscribePrice.toFixed(2) }) : t('profile.free')}</span>
                </div>
              </section>
            </aside>
          )}
        </div>
      </div>
      {mobile && mobileNav}
      {viewer && (
        <MediaViewer
          items={viewer.items}
          index={viewer.index}
          onIndex={(index) => setViewer((v) => (v ? { ...v, index } : v))}
          onClose={() => setViewer(null)}
          hasMore={false}
          onLoadMore={async () => undefined}
          mobile={mobile}
        />
      )}
      {toast}
    </div>
  )
}

function ProfileHeader({
  profile,
  canWrite,
  menu,
  setMenu,
  headerMenu,
  onStatus,
  onCopyLink,
  onEdit,
  highlights,
}: {
  profile: Profile
  canWrite: boolean
  menu: string | null
  setMenu: (m: null | 'header' | 'status' | 'sort') => void
  headerMenu: ReactNode
  onStatus: (visible: boolean) => void
  onCopyLink: () => void
  /** OnlyFans' Edit profile opens Settings → Profile. */
  onEdit: () => void
  highlights: Highlight[]
}) {
  const { t } = useTranslation('ofProfile')
  const cover = useMediaSrc(profile.header)
  return (
    <header className="ofp-head">
      <div ref={cover.ref as React.RefObject<HTMLDivElement>} className="ofp-cover" style={cover.src ? { backgroundImage: `url("${cover.src}")` } : undefined}>
        <div className="ofp-cover-shade">
          <div className="ofp-cover-text">
            <span className="ofp-cover-name">
              {profile.name}
              {profile.verified && <FeatherIcon name="check-circle" size={15} className="ofp-verified" />}
            </span>
            <span className="ofp-cover-counts">
              <span title={t('profile.photos')}>
                <FeatherIcon name="image" size={13} /> {compact(profile.photos)}
              </span>
              <span title={t('profile.videos')}>
                <FeatherIcon name="video" size={13} /> {compact(profile.videos)}
              </span>
              <span title={t('profile.likes')}>
                <FeatherIcon name="heart" size={13} /> {compact(profile.likes)}
              </span>
              <span title={t('profile.fans')}>
                <FeatherIcon name="users" size={13} /> {compact(profile.fans)}
              </span>
            </span>
          </div>
          <div className="lib-anchor">
            <button type="button" className="lib-icon-btn ofp-on-cover" aria-label={t('profile.actions')} onClick={() => setMenu(menu === 'header' ? null : 'header')}>
              <FeatherIcon name="more-vertical" size={18} />
            </button>
            {headerMenu}
          </div>
        </div>
      </div>
      <div className="ofp-head-row">
        <span className="ofp-head-avatar">
          <Avatar user={profile} size={88} />
          {profile.visibleOnline && <span className="ofp-online" aria-hidden="true" />}
        </span>
        <span className="lib-spacer" />
        {canWrite && (
          <button type="button" className="ofp-pill-btn" onClick={onEdit}>
            <FeatherIcon name="settings" size={15} />
            {t('profile.edit')}
          </button>
        )}
        <button type="button" className="ofp-round-btn" aria-label={t('profile.copyLink')} title={t('profile.copyLink')} onClick={onCopyLink}>
          <FeatherIcon name="share" size={17} />
        </button>
      </div>
      <div className="ofp-who">
        <h1 className="ofp-name">
          {profile.name}
          {profile.verified && <FeatherIcon name="check-circle" size={15} className="ofp-verified" />}
        </h1>
        <div className="ofp-handle">
          <span>@{profile.username}</span>
          <span aria-hidden="true">·</span>
          <span className="lib-anchor">
            <button type="button" className="ofp-status" disabled={!canWrite} aria-expanded={menu === 'status'} onClick={() => setMenu(menu === 'status' ? null : 'status')}>
              {profile.visibleOnline ? t('profile.available') : t('profile.invisible')}
              {canWrite && <FeatherIcon name="chevron-down" size={13} />}
            </button>
            {menu === 'status' && (
              <>
                <div className="lib-backdrop" onClick={() => setMenu(null)} />
                <div className="lib-pop lib-menu ofp-menu-left" role="menu" aria-label={t('profile.status')}>
                  {[true, false].map((v) => (
                    <button key={String(v)} type="button" role="menuitemradio" aria-checked={profile.visibleOnline === v} className="lib-menu-item" onClick={() => onStatus(v)}>
                      <span className={`ofp-radio ${profile.visibleOnline === v ? 'is-on' : ''}`} aria-hidden="true" />
                      <span className="lib-menu-label">{v ? t('profile.available') : t('profile.invisible')}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </span>
        </div>
        <OfHtml html={profile.about} className="ofp-text ofp-about" />
        {(profile.location || profile.website || profile.wishlist) && (
          <div className="ofp-facts">
            {profile.location && (
              <span>
                <FeatherIcon name="map-pin" size={13} /> {profile.location}
              </span>
            )}
            {profile.website && (
              <a className="ofp-link" href={profile.website} target="_blank" rel="noopener noreferrer">
                <FeatherIcon name="link" size={13} /> {profile.website.replace(/^https?:\/\//, '')}
              </a>
            )}
            {profile.wishlist && (
              <a className="ofp-link" href={profile.wishlist} target="_blank" rel="noopener noreferrer">
                <FeatherIcon name="gift" size={13} /> {t('profile.wishlist')}
              </a>
            )}
          </div>
        )}
      </div>
      {highlights.length > 0 && (
        <div className="ofp-highlights" aria-label={t('profile.highlights')}>
          {highlights.map((h) => (
            <HighlightCard key={h.id} h={h} />
          ))}
        </div>
      )}
    </header>
  )
}

function HighlightCard({ h }: { h: Highlight }) {
  const { ref, src } = useMediaSrc(h.cover)
  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className="ofp-highlight" style={src ? { backgroundImage: `url("${src}")` } : undefined}>
      <span className="ofp-highlight-title">{h.title}</span>
    </div>
  )
}

function PostTile({ post, onOpen }: { post: Post; onOpen: () => void }) {
  const { t } = useTranslation('ofProfile')
  const first = post.media[0]
  const { ref, src } = useMediaSrc(first?.thumb || first?.full || '')
  return (
    <button type="button" ref={ref as React.RefObject<HTMLButtonElement>} className="ofp-media-item ofp-tile" aria-label={t('post.open')} onClick={onOpen}>
      {first ? src ? <img src={src} alt="" /> : <span className="ofp-media-ph" /> : <OfHtml html={post.text} className="ofp-tile-text" />}
      <span className="ofp-tile-meta">
        {post.mediaCount > 0 && (
          <span>
            <FeatherIcon name={first?.type === 'video' ? 'video' : 'image'} size={11} /> {post.mediaCount}
          </span>
        )}
        {post.isPinned && <FeatherIcon name="bookmark" size={11} />}
      </span>
    </button>
  )
}

function Chip({ on, label, n, onPress }: { on: boolean; label: string; n?: number; onPress: () => void }) {
  return (
    <button type="button" role="radio" aria-checked={on} className={`lib-chip ${on ? 'is-on' : ''}`} onClick={onPress}>
      {label}
      {n !== undefined && <span className="lib-chip-n">{n.toLocaleString()}</span>}
    </button>
  )
}

function SortItem({ on, label, onPress }: { on: boolean; label: string; onPress: () => void }) {
  return (
    <button type="button" role="menuitemradio" aria-checked={on} className="lib-menu-item" onClick={onPress}>
      <span className={`ofp-radio ${on ? 'is-on' : ''}`} aria-hidden="true" />
      <span className="lib-menu-label">{label}</span>
    </button>
  )
}

function SearchBox({ value, onSearch }: { value: string; onSearch: (q: string) => void }) {
  const { t } = useTranslation('ofProfile')
  const [text, setText] = useState(value)
  useEffect(() => setText(value), [value])
  return (
    <form
      className="ofp-search"
      role="search"
      onSubmit={(e) => {
        e.preventDefault()
        onSearch(text)
      }}
    >
      <input className="ofp-input" aria-label={t('profile.searchPosts')} placeholder={t('profile.searchPosts')} value={text} onChange={(e) => setText(e.target.value)} />
      <button type="submit" className="lib-icon-btn" aria-label={t('profile.search')}>
        <FeatherIcon name="search" size={16} />
      </button>
    </form>
  )
}
